/**
 * Who owns an open artifact. Each mounted host registers its scope, and the
 * newest one is active: the shared setters write that scope's slot, so one
 * surface's preview never shows up in another's.
 */

import { atom, useSetAtom } from 'jotai';
import { useLayoutEffect } from 'react';

export type ArtifactScope =
  | { kind: 'home' }
  | { kind: 'chat'; projectId: string }
  | { kind: 'routine'; workflowId: string }
  | { kind: 'session'; sessionId: string };

export type ArtifactScopeKey =
  | 'home'
  | `chat:${string}`
  | `routine:${string}`
  | `session:${string}`
  | 'detached';

/** Writes made while no host is mounted land here, as they did in the one global atom. */
export const DETACHED_ARTIFACT_SCOPE: ArtifactScopeKey = 'detached';

export const artifactScopeKey = (scope: ArtifactScope): ArtifactScopeKey => {
  if (scope.kind === 'home') return 'home';
  if (scope.kind === 'chat') return `chat:${scope.projectId}`;
  if (scope.kind === 'routine') return `routine:${scope.workflowId}`;
  return `session:${scope.sessionId}`;
};

export const chatArtifactScopeKey = (projectId: string): ArtifactScopeKey =>
  `chat:${projectId}`;

/** The Work page's scope: a project is its chat; no project (the `work-home` sentinel) is Home. */
export const artifactScopeForProject = (projectId: string | undefined): ArtifactScope =>
  projectId && projectId !== 'work-home' ? { kind: 'chat', projectId } : { kind: 'home' };

const artifactScopeStackAtom = atom<readonly ArtifactScopeKey[]>([]);

export const activeArtifactScopeAtom = atom(
  (get) => get(artifactScopeStackAtom).at(-1) ?? DETACHED_ARTIFACT_SCOPE
);

/**
 * A host owns `scope` while mounted. Returns its key. `enabled: false` keeps a
 * host that delegates its canvas (to a nested host) off the stack, so it can't
 * take the active scope from the host that paints.
 */
export const useArtifactScope = (
  scope: ArtifactScope,
  { enabled = true }: { enabled?: boolean } = {}
): ArtifactScopeKey => {
  const key = artifactScopeKey(scope);
  const setStack = useSetAtom(artifactScopeStackAtom);
  // XXX: a layout effect so the store names the new scope before any passive effect writes through the setters.
  useLayoutEffect(() => {
    if (!enabled) return;
    setStack((stack) => [...stack, key]);
    return () =>
      setStack((stack) => {
        const index = stack.lastIndexOf(key);
        return index < 0 ? stack : [...stack.slice(0, index), ...stack.slice(index + 1)];
      });
  }, [enabled, key, setStack]);
  return key;
};
